import { useQuery } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import api from '../lib/api'

export interface SkillSuggestion {
  value: string
  source: 'capability' | 'demand'
  capability_id?: number
}

interface SimilarNudge {
  input: string
  suggestions: string[]
}

interface SkillSuggestionsResponse {
  suggestions: SkillSuggestion[]
  nudge: SimilarNudge | null
}

function useDebouncedValue(value: string, delay: number): string {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

/**
 * Suggestions for the skill pickers. An EMPTY query browses the catalog.
 *
 * `enabled` used to be `length >= 2`, which is why the demand-side picker
 * could not show the catalog until you had typed two characters of a word you
 * had no way to look up. The caller says when the list is wanted - normally
 * "while the input is open" - and an empty query is a legitimate request.
 */
export function useSkillSuggestions(query: string, enabled = true) {
  const debouncedQuery = useDebouncedValue(query, 250)

  return useQuery<SkillSuggestionsResponse>({
    queryKey: ['skill-suggestions', debouncedQuery],
    queryFn: async () => {
      const res = await api.get<SkillSuggestionsResponse>('/capabilities/suggestions', {
        params: { q: debouncedQuery },
      })
      return res.data
    },
    enabled,
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  })
}
